import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

export default function Header() {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'uk' ? 'en' : 'uk';
    i18n.changeLanguage(newLang);
  };

  const navLinks = [
    { id: 'home', label: t('nav.home') },
    { id: 'services', label: t('nav.services') },
    { id: 'brands', label: t('nav.brands') },
    { id: 'advantages', label: t('nav.advantages') },
    { id: 'contacts', label: t('nav.contacts') },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-xl shadow-sm border-b border-slate-100'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-18 py-4">
          {/* Logo */}
          <div className="flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/14febd11ed764a2e4131edc63c3ba26f/0a3ef7c86ce9fe9f73294dec3a9e17ad.png"
              alt="GOODFIX"
              className={`h-10 w-auto transition-all duration-300 ${isScrolled ? 'brightness-0' : 'drop-shadow-[0_0_12px_rgba(14,165,233,0.4)]'}`}
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-slate-600 hover:text-sky-600 hover:bg-sky-50'
                    : 'text-white/85 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Right Side */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={toggleLanguage}
              className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold border transition-all duration-200 whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? 'border-slate-200 text-slate-600 hover:border-sky-300 hover:text-sky-600 bg-white'
                  : 'border-white/25 text-white hover:bg-white/10'
              }`}
            >
              {i18n.language === 'uk' ? 'EN' : 'UA'}
            </button>
            <div className="flex items-center gap-2">
              <a
                href="https://t.me/goodfix_service"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className={`w-9 h-9 flex items-center justify-center rounded-xl transition-all duration-200 cursor-pointer ${
                  isScrolled
                    ? 'bg-sky-50 text-sky-500 hover:bg-sky-100'
                    : 'bg-white/15 text-white hover:bg-white/25'
                }`}
                title="Telegram"
              >
                <i className="ri-telegram-fill text-lg"></i>
              </a>
              <a
                href="viber://chat?number=%2B380937403000"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className={`w-9 h-9 flex items-center justify-center rounded-xl transition-all duration-200 cursor-pointer ${
                  isScrolled
                    ? 'bg-violet-50 text-violet-500 hover:bg-violet-100'
                    : 'bg-white/15 text-white hover:bg-white/25'
                }`}
                title="Viber"
              >
                <i className="ri-message-3-fill text-lg"></i>
              </a>
              <a
                href="https://www.instagram.com/goodfix.com.ua/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className={`w-9 h-9 flex items-center justify-center rounded-xl transition-all duration-200 cursor-pointer ${
                  isScrolled
                    ? 'bg-pink-50 text-pink-500 hover:bg-pink-100'
                    : 'bg-white/15 text-white hover:bg-white/25'
                }`}
                title="Instagram"
              >
                <i className="ri-instagram-fill text-lg"></i>
              </a>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg cursor-pointer transition-colors duration-200 ${
              isScrolled ? 'text-slate-700 hover:bg-slate-100' : 'text-white hover:bg-white/10'
            }`}
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          className="lg:hidden bg-white border-t border-slate-100 shadow-lg"
        >
          <div className="px-4 py-5 space-y-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="block w-full text-left px-4 py-3 rounded-xl text-slate-700 hover:text-sky-600 hover:bg-sky-50 text-sm font-medium transition-all duration-200 cursor-pointer"
              >
                {link.label}
              </button>
            ))}
            <div className="flex items-center gap-3 pt-3 border-t border-slate-100 mt-3">
              <button
                onClick={toggleLanguage}
                className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:border-sky-300 hover:text-sky-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {i18n.language === 'uk' ? 'EN' : 'UA'}
              </button>
              <div className="flex items-center gap-2">
                <a
                  href="https://t.me/goodfix_service"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-xl bg-sky-50 text-sky-500 hover:bg-sky-100 transition-all duration-200 cursor-pointer"
                  title="Telegram"
                >
                  <i className="ri-telegram-fill text-xl"></i>
                </a>
                <a
                  href="viber://chat?number=%2B380937403000"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-xl bg-violet-50 text-violet-500 hover:bg-violet-100 transition-all duration-200 cursor-pointer"
                  title="Viber"
                >
                  <i className="ri-message-3-fill text-xl"></i>
                </a>
                <a
                  href="https://www.instagram.com/goodfix.com.ua/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-xl bg-pink-50 text-pink-500 hover:bg-pink-100 transition-all duration-200 cursor-pointer"
                  title="Instagram"
                >
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
